/**
 * useLeaderboardPolling.ts - Safe Polling Hook for Leaderboard
 * Phase 3C - Implements D1 Safety Requirements
 * 
 * DIRECTIVE D1 COMPLIANCE:
 * ✅ Clear interval on unmount
 * ✅ Skip if loading guard (no overlapping requests)
 * ✅ Abort/ignore stale responses on pool change or tab leave
 * 
 * PHASE 3H-B FIX (Issue #014):
 * ✅ Fixed dependency cascade that caused 6000+ requests
 * ✅ Removed lastUpdated from fetchLeaderboard dependencies
 * ✅ Used ref for initial load check to prevent callback recreation
 * 
 * BUCKET 3: Added poolEventStatus for LIVE/FINAL badge
 */

import { useState, useEffect, useCallback, useRef } from 'react';

const POLL_INTERVAL = 30000; // 30 seconds per architect spec
const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api/v1';

interface LeaderboardStanding {
  userId: string;
  username: string;
  score: number;
  rank: number;
  status: string;
  totalUnits: number;
  isLocked: boolean;
  balance: number;
}

interface LeaderboardResponse {
  poolId?: string;
  pool_id?: string;
  poolName?: string;
  pool_name?: string;
  totalEntrants?: number;
  total_participants?: number;
  poolEventStatus?: string;  // BUCKET 3: "upcoming" | "live" | "final"
  standings?: LeaderboardStanding[];
  entries?: any[];
}

interface UseLeaderboardPollingResult {
  standings: LeaderboardStanding[];
  poolInfo: {
    poolId: string;
    poolName: string;
    totalEntrants: number;
    poolEventStatus: string;  // BUCKET 3: "upcoming" | "live" | "final"
  } | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  lastUpdated: Date | null;
  isPolling: boolean;
  togglePolling: () => void;
}

/**
 * Transform backend standing to UI format
 */
export const transformStandingToUI = (standing: LeaderboardStanding, myUserId?: string) => ({
  ...standing,
  isYou: standing.userId === myUserId,
  style: standing.totalUnits > 80 ? 'Concentrated' : standing.totalUnits > 50 ? 'Balanced' : 'Broad',
  streak: standing.isLocked ? 'Locked' : 'Building'
});

export function useLeaderboardPolling(poolId: string | undefined): UseLeaderboardPollingResult {
  // State
  const [standings, setStandings] = useState<LeaderboardStanding[]>([]);
  const [poolInfo, setPoolInfo] = useState<{ poolId: string; poolName: string; totalEntrants: number; poolEventStatus: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [isPolling, setIsPolling] = useState(true);
  
  // Refs for D1 safety
  const isLoadingRef = useRef(false);        // D1: Skip if loading guard
  const abortControllerRef = useRef<AbortController | null>(null);
  const poolIdRef = useRef(poolId);          // D1: Track pool changes
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasLoadedOnceRef = useRef(false);    // FIX: Track initial load without causing re-renders
  
  // Update ref when poolId changes
  useEffect(() => {
    poolIdRef.current = poolId;
    // Reset hasLoadedOnce when pool changes
    hasLoadedOnceRef.current = false;
  }, [poolId]);
  
  /**
   * Fetch leaderboard data with D1 safety guards
   * 
   * FIX: Removed lastUpdated from dependencies to prevent callback recreation
   * on every successful fetch, which was causing the polling useEffect to
   * recreate intervals thousands of times.
   */
  const fetchLeaderboard = useCallback(async (force: boolean = false) => {
    // D1: Skip if already loading (prevent overlap)
    if (!force && isLoadingRef.current) {
      console.log('[Leaderboard] Skipping fetch - already loading');
      return;
    }
    
    if (!poolId) {
      setError('No pool ID provided');
      setLoading(false);
      return;
    }
    
    // D1: Abort any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();
    const currentPoolId = poolId;
    
    isLoadingRef.current = true;
    // FIX: Use ref instead of state to check initial load
    if (!hasLoadedOnceRef.current) setLoading(true);
    
    try {
      const response = await fetch(
        `${API_BASE}/pools/${poolId}/leaderboard`,
        {
          signal: abortControllerRef.current.signal,
          headers: {
            'Content-Type': 'application/json',
            'X-User-ID': localStorage.getItem('userId') || 'test_user_001'
          }
        }
      );
      
      // D1: Check if pool changed during request (stale response)
      if (poolIdRef.current !== currentPoolId) {
        console.log('[Leaderboard] Ignoring stale response - pool changed');
        return;
      }
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data: LeaderboardResponse = await response.json();
      
      // D1: Final check before updating state
      if (poolIdRef.current !== currentPoolId) {
        console.log('[Leaderboard] Ignoring stale response - pool changed during parse');
        return;
      }
      
      // Handle both Pick Mode (standings) and GM Mode (entries) formats
      const rawStandings = data.standings || data.entries || [];
      
      // Transform GM format to expected format
      const normalizedStandings = rawStandings.map((s: any) => ({
        userId: s.userId || s.user_id,
        username: s.username || s.user_id || 'Unknown',
        score: s.score ?? s.current_balance ?? 0,
        rank: s.rank || 0,
        status: s.is_resolved ? 'resolved' : 'pending',
        totalUnits: s.totalUnits || s.total_units || 0,
        isLocked: s.isLocked ?? s.is_resolved ?? false,
        balance: s.balance || s.current_balance || 0
      }));
      
      setStandings(normalizedStandings);
      setPoolInfo({
        poolId: data.poolId || data.pool_id || '',
        poolName: data.poolName || data.pool_name || '',
        totalEntrants: data.totalEntrants || data.total_participants || rawStandings.length,
        poolEventStatus: data.poolEventStatus || 'upcoming'  // BUCKET 3: LIVE/FINAL badge
      });
      setError(null);
      setLastUpdated(new Date());
      hasLoadedOnceRef.current = true;  // FIX: Mark as loaded via ref
      
    } catch (err) {
      // D1: Ignore abort errors (expected when cancelling)
      if (err instanceof Error && err.name === 'AbortError') {
        console.log('[Leaderboard] Request aborted');
        return;
      }
      
      // D1: Check pool didn't change before setting error
      if (poolIdRef.current === currentPoolId) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      }
    } finally {
      isLoadingRef.current = false;
      setLoading(false);
    }
  }, [poolId]); // FIX: Removed lastUpdated - was causing dependency cascade
  
  /**
   * Manual refresh (bypasses loading guard)
   */
  const refresh = useCallback(() => {
    fetchLeaderboard(true);
  }, [fetchLeaderboard]);
  
  /**
   * Toggle polling on/off
   */
  const togglePolling = useCallback(() => {
    setIsPolling(prev => !prev);
  }, []);
  
  /**
   * Initial fetch on mount/pool change
   */
  useEffect(() => {
    // D1: Abort previous requests when pool changes
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Reset state on pool change
    setStandings([]);
    setPoolInfo(null);
    setLastUpdated(null);
    setError(null);
    
    // Initial fetch
    fetchLeaderboard(true);
    
  }, [poolId]); // Only re-run on poolId change - fetchLeaderboard is stable now
  
  /**
   * D1: Polling with interval cleanup
   * 
   * FIX: fetchLeaderboard is now stable (only depends on poolId),
   * so this effect won't re-run on every successful fetch.
   */
  useEffect(() => {
    // Check polling kill switch
    const disablePolling = import.meta.env.VITE_DISABLE_POLLING === 'true';
    
    if (!isPolling || disablePolling || !poolId) {
      // Clear any existing interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }
    
    // Set up polling interval
    intervalRef.current = setInterval(() => {
      // D1: Skip if loading (handled in fetchLeaderboard)
      fetchLeaderboard();
    }, POLL_INTERVAL);
    
    // D1: Cleanup on unmount or dependency change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isPolling, poolId, fetchLeaderboard]);
  
  /**
   * D1: Cleanup on unmount - abort any pending requests
   */
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);
  
  /**
   * D1: Pause polling when tab is hidden (ignore stale responses)
   */
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab hidden - abort current request
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
      } else {
        // Tab visible again - refresh immediately
        fetchLeaderboard(true);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchLeaderboard]);
  
  return {
    standings,
    poolInfo,
    loading,
    error,
    refresh,
    lastUpdated,
    isPolling,
    togglePolling
  };
}

export default useLeaderboardPolling;